import { CreateNewAction } from '@/components/features/files/actions/createNewAction'
import { DeleteAction } from '@/components/features/files/actions/deleteAction'
import {
  DirectoryItemCreate,
  DirectoryItem,
  DirectoryItemType
} from '@/components/features/files/types'
import { useFileManager } from '@/components/features/files/useFileManager'

type UploadTreeItem<T extends DirectoryItemType = 'directory'> = DirectoryItemCreate<
  T,
  { file?: File; status: string | null | boolean }
>

function createDirectoryTree(files: File[]): UploadTreeItem {
  const root: UploadTreeItem = {
    status: null,
    type: 'directory',
    name: 'root',
    children: []
  }

  for (const file of files) {
    let currentDirectory = root

    const relativePath = file.webkitRelativePath || ''
    const directoryParts = relativePath ? relativePath.split('/').slice(0, -1) : []

    for (const directoryName of directoryParts) {
      let subDirectory = currentDirectory.children?.find((dir) => dir.name === directoryName) as
        | UploadTreeItem
        | undefined

      if (!subDirectory) {
        subDirectory = {
          type: 'directory',
          status: null,
          name: directoryName,
          children: []
        }
        currentDirectory.children?.push(subDirectory)
      }
      currentDirectory = subDirectory
    }

    currentDirectory.children?.push({
      type: 'file',
      status: null,
      name: file.name,
      file
    })
  }

  return root
}

export default function () {
  const fileManager = useFileManager<{ status: string | boolean | null }>()
  const tree = fileManager.tree

  function addFiles(newFiles: File[]) {
    const tree = createDirectoryTree(newFiles)
    if (tree.children) {
      fileManager.executeAction(
        new CreateNewAction(tree.children, fileManager.currentDirectoryId.value)
      )
    }
  }

  async function addEntries(entries: FileSystemEntry[]) {
    async function getAllEntriesFromReader(
      dirReader: FileSystemDirectoryReader
    ): Promise<FileSystemEntry[]> {
      let allEntries: FileSystemEntry[] = []
      let currentBatch: FileSystemEntry[]
      do {
        currentBatch = await new Promise<FileSystemEntry[]>((resolve, reject) => {
          dirReader.readEntries(resolve, reject)
        })
        if (currentBatch.length > 0) {
          allEntries = allEntries.concat(currentBatch)
        }
      } while (currentBatch.length > 0)
      return allEntries
    }

    async function processEntryRecursive(
      entry: FileSystemEntry
    ): Promise<UploadTreeItem<DirectoryItemType> | null> {
      if (entry.isFile) {
        const file = await new Promise<File>((resolve, reject) => {
          ;(entry as FileSystemFileEntry).file(resolve, reject)
        })
        return {
          type: 'file',
          status: null,
          name: entry.name,
          file: file
        }
      } else if (entry.isDirectory) {
        const directoryEntry = entry as FileSystemDirectoryEntry
        const dirReader = directoryEntry.createReader()
        const subEntries = await getAllEntriesFromReader(dirReader)

        const directoryNode: UploadTreeItem = {
          type: 'directory',
          status: null,
          name: entry.name,
          children: []
        }

        for (const subEntry of subEntries) {
          // The parentId for children is the current directory's id
          const childNode = await processEntryRecursive(subEntry)
          if (childNode) {
            directoryNode.children?.push(childNode)
          }
        }
        return directoryNode
      }
      console.warn(`Skipping unhandled entry type: ${entry.name}`)
      return null
    }

    const processedTreeItems: UploadTreeItem<DirectoryItemType>[] = []

    for (const entry of entries) {
      const treeItem = await processEntryRecursive(entry)
      if (treeItem) {
        processedTreeItems.push(treeItem)
      }
    }

    if (processedTreeItems.length > 0) {
      fileManager.executeAction(
        new CreateNewAction(processedTreeItems, fileManager.currentDirectoryId.value)
      )
    }
  }

  function removeItem(file: DirectoryItem<DirectoryItemType, { status: string | boolean | null }>) {
    fileManager.executeAction(new DeleteAction([file]))
  }

  return { tree, fileManager, addFiles, addEntries, removeItem }
}
